import { AISystem } from '../config/ai';
import { AIAdapter } from './AIAdapter';
import { GeminiAdapter } from './GeminiAdapter';
import { OpenAIAdapter } from './OpenAIAdapter';
import { AnthropicAdapter } from './AnthropicAdapter';
import { PerplexityAdapter } from './PerplexityAdapter';

/**
 * Factory for creating AI adapters
 * This provides a clean interface for getting the appropriate adapter
 * and makes it easy to add new AI systems in the future
 */
export class AdapterFactory {
  private static adapters: Map<AISystem, AIAdapter> = new Map();

  /**
   * Get an adapter for the specified AI system
   * @param system The AI system to get an adapter for
   * @returns The appropriate AI adapter
   */
  static getAdapter(system: AISystem): AIAdapter {
    // Check if we already have this adapter cached
    if (this.adapters.has(system)) {
      return this.adapters.get(system)!;
    }

    // Create the appropriate adapter
    let adapter: AIAdapter;

    switch (system) {
      case 'gemini':
        adapter = new GeminiAdapter();
        break;
      case 'openai':
        adapter = new OpenAIAdapter('openai');
        break;
      case 'anthropic':
        adapter = new AnthropicAdapter();
        break;
      case 'perplexity':
        adapter = new PerplexityAdapter();
        break;
      case 'copilot':
        // Copilot uses the same API as OpenAI
        adapter = new OpenAIAdapter('copilot');
        break;
      default:
        throw new Error(`Unknown AI system: ${system}`);
    }

    // Cache it for future use
    this.adapters.set(system, adapter);
    return adapter;
  }

  /**
   * Get all configured adapters
   * @returns Array of configured AI adapters
   */
  static getConfiguredAdapters(): AIAdapter[] {
    const systems: AISystem[] = ['gemini', 'openai', 'anthropic', 'perplexity', 'copilot'];
    return systems
      .map((system) => this.getAdapter(system))
      .filter((adapter) => adapter.isConfigured());
  }
}
